/**
 * 画面共通レイアウト
 * 来場者画面で共通利用するタイトル・説明付きレイアウトを提供します
 */

import React from "react";
import {
  Modal,
  Pressable,
  SafeAreaView,
  ScrollView,
  Text,
  View,
  type ViewStyle,
} from "./WebPrimitives";
import PixelHeroMascot from "./PixelHeroMascot";
import OfflineStatusBanner from "./OfflineStatusBanner";
import AppUpdateBanner from "./AppUpdateBanner";
import { ConfirmDialogHost } from "./ConfirmDialog";
import { tourAnchor } from "./guidedTourAnchor";
import TextSizeControl from "./TextSizeControl";
import { logError } from "../utils/logger";
import useCompactViewport from "../hooks/useCompactViewport";

/** テーマ別表示設定 */
const THEME_CLASS_NAME_MAP = Object.freeze({
  visitor: {
    root: "bg-app-quest-sky",
    panel: "border-4 border-app-quest-night bg-app-panel",
    title: "text-black",
    description: "text-app-text",
    badge:
      "border-2 border-app-quest-night bg-app-quest-gold text-app-quest-night",
    shadow: "shadow-[6px_6px_0_rgba(22,36,58,0.34)]",
  },
  neutral: {
    root: "bg-[#8BB9FF]",
    panel: "border-4 border-app-quest-night bg-app-panel",
    title: "text-app-text",
    description: "text-app-text",
    badge:
      "border-2 border-app-quest-night bg-app-quest-gold text-app-quest-night",
    shadow: "shadow-[6px_6px_0_rgba(22,36,58,0.34)]",
  },
});

/** 背景ドットブロック定義 */
const PIXEL_BACKGROUND_BLOCKS = Object.freeze([
  { top: "3%", left: "6%", size: 14, tone: "cloud" },
  { top: "7%", left: "19%", size: 10, tone: "gold" },
  { top: "12%", left: "79%", size: 16, tone: "cloud" },
  { top: "16%", left: "90%", size: 9, tone: "night" },
  { top: "20%", left: "4%", size: 11, tone: "forest" },
  { top: "24%", left: "30%", size: 8, tone: "cloud" },
  { top: "28%", left: "70%", size: 10, tone: "gold" },
  { top: "34%", left: "84%", size: 12, tone: "cloud" },
  { top: "38%", left: "12%", size: 9, tone: "night" },
  { top: "42%", left: "24%", size: 13, tone: "forest" },
  { top: "48%", left: "74%", size: 8, tone: "cloud" },
  { top: "52%", left: "90%", size: 11, tone: "gold" },
  { top: "58%", left: "6%", size: 15, tone: "cloud" },
  { top: "63%", left: "28%", size: 8, tone: "forest" },
  { top: "67%", left: "46%", size: 9, tone: "night" },
  { top: "72%", left: "80%", size: 14, tone: "cloud" },
  { top: "76%", left: "2%", size: 11, tone: "gold" },
  { top: "82%", left: "16%", size: 9, tone: "cloud" },
  { top: "86%", left: "58%", size: 12, tone: "forest" },
  { top: "90%", left: "90%", size: 8, tone: "night" },
]);

/** 背景ドットブロック色 */
const PIXEL_BACKGROUND_TONE_COLOR_MAP = Object.freeze({
  cloud: "#D6EBFF",
  gold: "#FFD46A",
  forest: "#7DBB73",
  night: "#2E4A75",
});

/** タイトル用ピクセル影 */
const PIXEL_TITLE_STYLE = Object.freeze({
  textShadowColor: "#40F7B1",
  textShadowOffset: { width: 2, height: 1 },
  textShadowRadius: 0,
});

/** 全画面のヘルプ末尾へ表示するサポート案内 */
const SCREEN_HELP_SUPPORT_MESSAGE =
  "操作に困った場合や分からないことがある場合は、B館前テントのスタッフまでお越しください。";

/** 画面共通レイアウトのトースト通知 */
type ScreenLayoutToast = {
  message: string;
  type: "success" | "error";
};

/** 画面共通レイアウトのプロパティ */
type ScreenLayoutProps = {
  title: string;
  description: string;
  theme?: "visitor" | "neutral";
  badge?: string;
  showHeader?: boolean;
  fullWidth?: boolean;
  showMascot?: boolean;
  mascotMood?: "normal" | "happy" | "focus" | "trouble";
  mascotPose?: "idle" | "win" | "guide";
  titleClassName?: string;
  headerLeftAction?: React.ReactNode;
  titleLeadingAction?: React.ReactNode;
  titleTrailingAction?: React.ReactNode;
  helpItems?: string[];
  /** ヘッダーの下・本文の上に置く帯。ログイン後の画面だけが渡す */
  topSlot?: React.ReactNode;
  bottomBar?: React.ReactNode;
  toast?: ScreenLayoutToast | null;
  onRefresh?: () => void | Promise<unknown>;
  refreshDisabled?: boolean;
  children: React.ReactNode;
};

/**
 * 画面共通レイアウトコンポーネント
 * @param {Object} props - プロパティ
 * @param {string} props.title - 画面タイトル
 * @param {string} props.description - 画面説明（ヘッダーへは描画せず、?のヘルプモーダル内でのみ表示する）
 * @param {'visitor' | 'neutral'} [props.theme='neutral'] - 表示テーマ
 * @param {string} [props.badge=''] - 旧仕様のバッジ文字列（互換のため受け取るが、現在のヘッダーには描画しない）
 * @param {boolean} [props.showHeader] - ヘッダー表示可否（未指定時は表示）
 * @param {boolean} [props.fullWidth=false] - コンテンツを全幅で表示するか
 * @param {boolean} [props.showMascot] - ドット勇者表示可否
 * @param {'normal' | 'happy' | 'focus' | 'trouble'} [props.mascotMood='normal'] - ドット勇者の表情
 * @param {'idle' | 'win' | 'guide'} [props.mascotPose='idle'] - ドット勇者のポーズ
 * @param {string} [props.titleClassName=''] - タイトル文字のクラス名上書き
 * @param {React.ReactNode} [props.headerLeftAction] - ヘッダー1行内の戻るボタン等（タイトルより前に表示）
 * @param {React.ReactNode} [props.titleLeadingAction] - ヘッダー1行内でタイトルの直前に表示するアクション要素
 * @param {React.ReactNode} [props.titleTrailingAction] - ヘッダー1行内でタイトルの直後・?ボタンの前に表示するアクション要素
 * @param {string[]} [props.helpItems=[]] - ヘルプへ追加表示する主な操作説明
 * @param {React.ReactNode} [props.topSlot] - ヘッダーの下・本文の上に置く帯（ログイン後の画面だけが渡す）
 * @param {React.ReactNode} [props.bottomBar] - 画面下部に表示する補助ナビゲーション
 * @param {{ message: string, type: 'success' | 'error' }} [props.toast] - トースト通知
 * @param {() => void | Promise<unknown>} [props.onRefresh] - 画面状態を保持して再取得する処理（未指定時はページ再読込）
 * @param {boolean} [props.refreshDisabled=false] - 送信中など更新を禁止する状態
 * @param {React.ReactNode} props.children - 子要素
 * @returns {JSX.Element} 画面共通レイアウト
 */
const ScreenLayout = ({
  title,
  description,
  theme = "neutral",
  badge = "",
  showHeader,
  fullWidth = false,
  showMascot,
  mascotMood = "normal",
  mascotPose = "idle",
  titleClassName = "",
  headerLeftAction = null,
  titleLeadingAction = null,
  titleTrailingAction = null,
  helpItems = [],
  topSlot = null,
  bottomBar = null,
  toast = null,
  onRefresh,
  refreshDisabled = false,
  children,
}: ScreenLayoutProps) => {
  /** 小型スマートフォン・横向き向けの圧縮表示か */
  const { isCompact: isCompactViewport } = useCompactViewport();
  /** ページヘルプの表示状態 */
  const [isHelpVisible, setIsHelpVisible] = React.useState(false);
  /** 更新中の連打を防ぐ状態と同期ガード */
  const [isRefreshing, setIsRefreshing] = React.useState(false);
  const refreshInFlightRef = React.useRef(false);
  /** 共通更新処理で発生したエラー */
  const [refreshError, setRefreshError] = React.useState("");

  /**
   * 現在画面を更新し、専用処理がある画面では入力とQRを保持する
   * @returns {Promise<void>} 更新処理の完了
   */
  const handleRefresh = async (): Promise<void> => {
    if (refreshInFlightRef.current || refreshDisabled) {
      return;
    }
    refreshInFlightRef.current = true;
    setIsRefreshing(true);
    setRefreshError("");
    try {
      if (onRefresh) {
        await onRefresh();
      } else {
        window.location.reload();
      }
    } catch (error) {
      logError("画面の更新に失敗しました。", {
        errorMessage: error instanceof Error ? error.message : String(error),
      });
      setRefreshError("画面を更新できませんでした。もう一度お試しください。");
    } finally {
      refreshInFlightRef.current = false;
      setIsRefreshing(false);
    }
  };
  /** テーマクラス */
  const themeClassName =
    THEME_CLASS_NAME_MAP[theme] ?? THEME_CLASS_NAME_MAP.neutral;
  /** タイトルクラス上書き有無 */
  const hasTitleOverride =
    typeof titleClassName === "string" && titleClassName.trim() !== "";
  /** ヘッダー表示可否 */
  const shouldShowHeader = typeof showHeader === "boolean" ? showHeader : true;
  /** マスコット表示可否 */
  const shouldShowMascot = typeof showMascot === "boolean" ? showMascot : false;
  /** 正規化済みタイトル */
  const normalizedTitle = typeof title === "string" ? title.trim() : "";
  /** 正規化済み説明（ヘッダーには描画せず、?のヘルプモーダル側で表示する） */
  const normalizedDescription =
    typeof description === "string" ? description.trim() : "";
  /** 正規化済みヘルプ項目 */
  const normalizedHelpItems = Array.isArray(helpItems)
    ? helpItems
        .map((item) => (typeof item === "string" ? item.trim() : ""))
        .filter(Boolean)
    : [];
  /** コンテナクラス */
  const containerClassName = fullWidth
    ? `mx-auto min-h-0 flex-1 w-full px-2 ${isCompactViewport ? "py-1" : "py-2"}`
    : `mx-auto min-h-0 flex-1 w-full max-w-[1240px] px-3 ${isCompactViewport ? "py-1" : "py-3"}`;

  /**
   * ページヘルプを開く
   * @returns {void}
   */
  const handleOpenHelp = () => {
    setIsHelpVisible(true);
  };

  /**
   * ページヘルプを閉じる
   * @returns {void}
   */
  const handleCloseHelp = () => {
    setIsHelpVisible(false);
  };

  return (
    <SafeAreaView
      className={`visitor-theme-shell h-full min-h-0 flex-1 overflow-hidden ${themeClassName.root}`}
    >
      <a
        className="sr-only z-[10001] border-2 border-app-quest-night bg-white px-4 py-3 text-app-quest-night focus:not-sr-only focus:fixed focus:left-2 focus:top-2"
        href="#screen-main-content"
      >
        本文へスキップ
      </a>
      <View className="absolute inset-0 z-0" pointerEvents="none">
        {PIXEL_BACKGROUND_BLOCKS.map((block, index) => (
          <View
            key={`pixel-bg-${index}`}
            style={{
              backgroundColor:
                PIXEL_BACKGROUND_TONE_COLOR_MAP[block.tone] ?? "#D6EBFF",
              height: block.size,
              left: block.left,
              opacity: 0.4,
              position: "absolute",
              top: block.top,
              width: block.size,
            } as ViewStyle}
          />
        ))}
      </View>
      <View className={`visitor-theme-content relative z-10 ${containerClassName}`}>
        <OfflineStatusBanner />
        <AppUpdateBanner reloadDisabled={refreshDisabled} />
        {shouldShowHeader ? (
          // ヘッダーは1行に収め、badge・descriptionはここへ描画しない（descriptionは?のヘルプモーダル側で表示する）
          <View
            className={`${isCompactViewport ? "mb-2 px-3 py-2" : "mb-4 px-4 py-4"} ${themeClassName.panel} ${themeClassName.shadow}`}
          >
            <View className={`${isCompactViewport ? "gap-1" : "gap-2"} flex-row items-center`}>
              {headerLeftAction}
              {titleLeadingAction}
              <Text
                aria-level={1}
                className={`${isCompactViewport ? "text-[17px] leading-6" : "text-[22px]"} min-w-0 flex-1 font-extrabold tracking-[1px] ${hasTitleOverride ? titleClassName : themeClassName.title}`}
                role="heading"
                style={
                  theme === "visitor" || hasTitleOverride
                    ? undefined
                    : PIXEL_TITLE_STYLE
                }
              >
                {title}
              </Text>
              {shouldShowMascot ? (
                <PixelHeroMascot
                  mood={mascotMood}
                  pixelSize={3}
                  pose={mascotPose}
                  variant="standard"
                />
              ) : null}
              <View className={`${isCompactViewport ? "gap-1" : "gap-2"} shrink-0 flex-row items-center`}>
                {titleTrailingAction}
                <Pressable
                  accessibilityHint="この画面の情報を再取得します"
                  accessibilityLabel={isRefreshing ? "画面を更新中" : "画面を更新"}
                  accessibilityRole="button"
                  className="h-11 w-11 items-center justify-center border-2 border-app-quest-night bg-[#EAF3FF] disabled:opacity-50"
                  disabled={isRefreshing || refreshDisabled}
                  onPress={handleRefresh}
                  testID="screen-refresh-button"
                >
                  <Text accessible={false} className="text-lg leading-5 font-extrabold text-app-quest-night">
                    ↻
                  </Text>
                  <Text accessible={false} className="text-[9px] leading-3 font-bold text-app-quest-night">
                    更新
                  </Text>
                </Pressable>
                <Pressable
                  accessibilityHint={`${normalizedTitle || "このページ"}の説明を表示します`}
                  accessibilityLabel="このページの使い方を表示"
                  accessibilityRole="button"
                  className="h-11 w-11 items-center justify-center rounded-none border-2 border-app-quest-night bg-app-accent-soft"
                  onPress={handleOpenHelp}
                  testID="screen-help-button"
                  {...tourAnchor("screen.help")}
                >
                  <Text
                    accessible={false}
                    className="text-xl font-extrabold text-app-quest-night"
                  >
                    ?
                  </Text>
                </Pressable>
              </View>
            </View>
          </View>
        ) : null}
        {topSlot}
        <View
          className="min-h-0 flex-1 overflow-hidden"
          id="screen-main-content"
          role="main"
          tabIndex={-1}
        >
          {refreshError ? (
            <Text role="alert" className="mb-2 text-sm text-app-danger">
              {refreshError}
            </Text>
          ) : null}
          {children}
        </View>
        {bottomBar ? (
          <View className={`${isCompactViewport ? "pt-1" : "pt-3"} relative z-20 shrink-0`}>
            {bottomBar}
          </View>
        ) : null}
      </View>
      <Modal
        accessibilityLabel={`${normalizedTitle || "このページ"}の使い方`}
        animationType="fade"
        onRequestClose={handleCloseHelp}
        transparent
        visible={isHelpVisible}
      >
        <View className="min-h-0 flex-1 justify-center overflow-hidden bg-black/60 px-4 py-8">
          <View className="mx-auto flex max-h-full min-h-0 w-full max-w-[560px] flex-col overflow-hidden border-4 border-app-quest-night bg-app-card p-4 shadow-[6px_6px_0_rgba(22,36,58,0.38)]">
            <View className="shrink-0 flex-row items-start justify-between gap-3 border-b-2 border-app-quest-night pb-3">
              <View className="min-w-0 flex-1">
                <Text className="text-[11px] font-extrabold tracking-[1px] text-app-text-secondary">
                  PAGE GUIDE
                </Text>
                <Text
                  aria-level={2}
                  className="mt-1 text-xl font-extrabold text-app-quest-night"
                  role="heading"
                >
                  {normalizedTitle || "このページの使い方"}
                </Text>
              </View>
              <Pressable
                accessibilityLabel="ページの説明を閉じる"
                accessibilityRole="button"
                className="h-11 w-11 items-center justify-center border-2 border-app-quest-night bg-app-panel"
                onPress={handleCloseHelp}
                testID="screen-help-close-button"
              >
                <Text
                  accessible={false}
                  className="text-lg font-extrabold text-app-quest-night"
                >
                  ×
                </Text>
              </Pressable>
            </View>
            <ScrollView
              className="mt-3 min-h-0 flex-1"
              contentContainerStyle={{ paddingBottom: 4 }}
              showsVerticalScrollIndicator={false}
            >
              <TextSizeControl />
              <Text className="mt-4 text-sm leading-6 text-app-text">
                {normalizedDescription || "このページの案内と操作方法を確認できます。"}
              </Text>
              {normalizedHelpItems.length > 0 ? (
                <View className="mt-4 border-2 border-app-border bg-app-card-soft p-3">
                  <Text className="text-sm font-extrabold text-app-quest-night">
                    このページでできること
                  </Text>
                  {normalizedHelpItems.map((item, index) => (
                    <View
                      className="mt-2 flex-row items-start gap-2"
                      key={`${normalizedTitle}-help-${index}`}
                    >
                      <Text
                        accessible={false}
                        className="text-sm font-extrabold text-app-quest-night"
                      >
                        ◆
                      </Text>
                      <Text className="min-w-0 flex-1 text-sm leading-6 text-app-text">
                        {item}
                      </Text>
                    </View>
                  ))}
                </View>
              ) : null}
              <View className="mt-4 border-2 border-app-quest-gold bg-amber-50 p-3">
                <Text className="text-sm font-extrabold text-app-quest-night">
                  困ったときは
                </Text>
                <Text className="mt-1 text-sm leading-6 text-app-text">
                  {SCREEN_HELP_SUPPORT_MESSAGE}
                </Text>
              </View>
            </ScrollView>
          </View>
        </View>
      </Modal>
      <ConfirmDialogHost />
      {toast?.message ? (
        <View
          className={`absolute left-[calc(1rem+env(safe-area-inset-left))] right-[calc(1rem+env(safe-area-inset-right))] top-[calc(1.5rem+env(safe-area-inset-top))] rounded-xl px-4 py-3 shadow-lg ${toast.type === "error" ? "bg-[#C5221F]" : "bg-[#137333]"}`}
          pointerEvents="none"
          style={{ zIndex: 9999 }}
        >
          <Text className="text-sm font-semibold text-white">
            {toast.type === "error" ? "⚠ " : "✓ "}
            {toast.message}
          </Text>
        </View>
      ) : null}
    </SafeAreaView>
  );
};

export default ScreenLayout;
